import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { clubsBookingApi } from '@/api/clubs-booking';
import { AdminSection } from '@/components/admin/AdminLayout';
import AdminShell from '@/components/admin/AdminShell';

const UsersEditPage = () => {
  const navigate = useNavigate();
  const { userId = '' } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    name: '',
    email: '',
    role: 'user' as 'admin' | 'user',
    password: '',
    confirmPassword: '',
  });

  useEffect(() => {
    let active = true;
    const loadUser = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await clubsBookingApi.getOrganizationUser(userId);
        if (!active) return;
        setForm((current) => ({
          ...current,
          name: response.data.name || '',
          email: response.data.email || '',
          role: response.data.role === 'admin' ? 'admin' : 'user',
        }));
      } catch (err: any) {
        if (!active) return;
        const messageText = err?.response?.data?.message;
        setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not load user');
      } finally {
        if (active) setLoading(false);
      }
    };

    void loadUser();
    return () => {
      active = false;
    };
  }, [userId]);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setSaving(true);

    try {
      await clubsBookingApi.updateOrganizationUser(userId, {
        name: form.name,
        email: form.email,
        role: form.role,
        password: form.password || undefined,
        confirmPassword: form.confirmPassword || undefined,
      });
      navigate('/users');
    } catch (err: any) {
      const rawError = err?.response?.data?.errors;
      const messageText = err?.response?.data?.message;

      if (Array.isArray(rawError) && rawError.length > 0) {
        setError(String(rawError[0]?.message || 'Could not update user'));
      } else {
        setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not update user');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminShell title="Users" subtitle="Update a team member's details and access level">
      <AdminSection>
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-2xl font-semibold">Edit user</h2>
          <Link to="/users" className="rounded-md border border-slate-300 px-3 py-1.5 text-sm hover:bg-slate-50">Back to list</Link>
        </div>
        {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
        {loading ? (
          <p className="text-sm text-slate-500">Loading user...</p>
        ) : (
          <form onSubmit={onSubmit} className="grid gap-4 md:grid-cols-2">
            <label className="text-sm text-slate-600">Full name<input className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={form.name} onChange={(e) => setForm((v) => ({ ...v, name: e.target.value }))} required /></label>
            <label className="text-sm text-slate-600">Email<input type="email" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={form.email} onChange={(e) => setForm((v) => ({ ...v, email: e.target.value }))} required /></label>
            <label className="text-sm text-slate-600">
              Role
              <select className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={form.role} onChange={(e) => setForm((v) => ({ ...v, role: e.target.value as 'admin' | 'user' }))}>
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </label>
            <div className="text-sm text-slate-500 md:self-end">
              Leave the password fields blank if you only want to change their name, email, or role.
            </div>
            <label className="text-sm text-slate-600">New password<input type="password" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={form.password} onChange={(e) => setForm((v) => ({ ...v, password: e.target.value }))} minLength={6} /></label>
            <label className="text-sm text-slate-600">Confirm new password<input type="password" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" value={form.confirmPassword} onChange={(e) => setForm((v) => ({ ...v, confirmPassword: e.target.value }))} minLength={6} /></label>
            <div className="md:col-span-2"><button className="rounded-lg bg-[#1F8F5F] px-5 py-2.5 font-medium text-white hover:bg-[#1F8F5F] disabled:opacity-60" disabled={saving}>{saving ? 'Saving...' : 'Save changes'}</button></div>
          </form>
        )}
      </AdminSection>
    </AdminShell>
  );
};

export default UsersEditPage;
